import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { formatParcours } from "../../services/aspirantUtils";

// Fonction pour calculer l'âge à partir de la date de naissance
function calcAge(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? `${age} ans` : "—";
}

export default function EglisesAspirants() {
  const [eglises, setEglises] = useState([]);
  const [aspirants, setAspirants] = useState([]);
  const [selectedEglise, setSelectedEglise] = useState(null); // 👈 objet église
  const [progressCGByAspirant, setProgressCGByAspirant] = useState({});
  const [progressSYLByAspirant, setProgressSYLByAspirant] = useState({});

  useEffect(() => {
    api
      .get("/districtStats/eglises")
      .then((res) => setEglises(res.data || []))
      .catch((err) => console.error("Erreur chargement églises:", err));
  }, []);

  const handleClickEglise = (egliseObj) => {
    setSelectedEglise(egliseObj);
    api
      .get(`/districtStats/eglise/${egliseObj.eglise_id}/aspirants`)
      .then((res) => setAspirants(res.data || []))
      .catch((err) => console.error("Erreur chargement aspirants:", err));
  };

  // Charger les progress quand une église est sélectionnée
  useEffect(() => {
    if (!selectedEglise?.eglise_id) return;

    const loadProgress = async () => {
      try {
        const [cg, syl] = await Promise.all([
          api
            .get(`/districtStats/eglise/${selectedEglise.eglise_id}/progress/CG`)
            .then((res) => res.data),
          api
            .get(`/districtStats/eglise/${selectedEglise.eglise_id}/progress/SYL`)
            .then((res) => res.data),
        ]);

        const toMap = (arr) => {
          const map = {};
          (arr || []).forEach((r) => {
            const id = r.aspirant_id ?? r.id ?? r.user_id;
            if (id == null) return;
            const done = Number(r.done) || 0;
            const total = Number(r.total) || 0;
            let percent = Number(r.progress);
            if (!Number.isFinite(percent)) percent = 0;
            if (total > 0) {
              const p = Math.round((done / total) * 100);
              if (Number.isFinite(p)) percent = p;
            }
            percent = Math.max(0, Math.min(100, percent));
            map[String(id)] = { done, total, percent };
          });
          return map;
        };

        setProgressCGByAspirant(toMap(cg));
        setProgressSYLByAspirant(toMap(syl));
      } catch (e) {
        console.error("Erreur lors du chargement des progressions:", e);
        setProgressCGByAspirant({});
        setProgressSYLByAspirant({});
      }
    };

    loadProgress();
  }, [selectedEglise]);

  const barClassFor = (percent) => {
    if (percent >= 75) return "bg-success";
    if (percent >= 40) return "bg-warning";
    return "bg-danger";
  };

  return (
    <div className="card shadow-sm mt-4">
      <div className="card-header bg-primary text-white">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column">
            {/* Titre */}
            <strong>Églises de votre district</strong>
            {/* Fil d'Ariane dynamique */}
            <div className="small text-white-50">
              {!selectedEglise ? (
                "Liste des églises"
              ) : (
                <>
                  <span
                    className="text-white text-decoration-underline"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      // retour à la liste
                      setSelectedEglise(null);
                      setAspirants([]);
                      setProgressCGByAspirant({});
                      setProgressSYLByAspirant({});
                    }}
                  >
                    Églises
                  </span>
                  <span className="mx-1">/</span>
                  <span className="text-white">{selectedEglise.eglise_name}</span>
                  <span className="mx-1">/</span>
                  <span>👥 Élèves</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-body">
        {/* --- LISTE EGLISES --- */}
        {!selectedEglise && (
          <div className="row g-3">
            {eglises.length === 0 ? (
              <div className="text-muted">Aucune église.</div>
            ) : (
              eglises.map((e) => (
                <div key={e.eglise_id} className="col-md-4">
                  <div
                    className="p-3 border rounded shadow-sm bg-white d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleClickEglise(e)} // 👈 on passe l'objet complet
                  >
                    <div>
                      <div className="fw-semibold">{e.eglise_name}</div>
                      <div className="small text-muted">
                        <span className="badge bg-primary me-2">CG: {e.total_CG}</span>
                        <span className="badge bg-syl">SYL: {e.total_SYL}</span>
                      </div>
                    </div>
                    <i className="bi bi-chevron-right"></i>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* --- LISTE ASPIRANTS --- */}
        {selectedEglise && (
          <>
            {/* Version desktop : tableau */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-hover table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Nom</th>
                    <th>Matricule</th>
                    <th>Âge</th>
                    <th>Parcours</th>
                    <th>Progression</th>
                  </tr>
                </thead>
                <tbody>
                  {aspirants.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        Aucun élève
                      </td>
                    </tr>
                  ) : (
                    aspirants.map((a, idx) => {
                      const prog =
                        a.parcours === "Chef Guide"
                          ? progressCGByAspirant[a.id]
                          : a.parcours === "Senior Youth Leader"
                          ? progressSYLByAspirant[a.id]
                          : undefined;

                      const done = prog?.done ?? 0;
                      const total = prog?.total ?? 0;
                      const percent = prog?.percent ?? 0;

                      const barClass = barClassFor(percent);
                      const label = total > 0 ? `${done}/${total}` : `${done}/—`;

                      return (
                        <tr key={a.id}>
                          <td>{idx + 1}</td>
                          <td className="fw-semibold">{a.aspirant_name}</td>
                          <td>
                            {a.matricule ? (
                              <span className="badge bg-success text-white">{a.matricule}</span>
                            ) : (
                              <span className="badge bg-warning text-dark">En attente</span>
                            )}
                          </td>
                          <td>{calcAge(a.birth_date)}</td>
                          <td>
                            <span className="badge bg-info text-dark">{formatParcours(a.parcours)}</span>
                          </td>
                          <td style={{ minWidth: 260 }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span className="badge bg-light text-dark">
                                {label} ({percent}%)
                              </span>
                            </div>
                            <div className="progress" style={{ height: 18 }}>
                              <div
                                className={`progress-bar ${barClass}`}
                                role="progressbar"
                                style={{ width: `${percent}%` }}
                                aria-valuenow={percent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Version mobile : cartes */}
            <div className="d-md-none">
              {aspirants.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  Aucun élève
                </div>
              ) : (
                aspirants.map((a, idx) => {
                  const prog =
                    a.parcours === "Chef Guide"
                      ? progressCGByAspirant[a.id]
                      : a.parcours === "Senior Youth Leader"
                      ? progressSYLByAspirant[a.id]
                      : undefined;

                  const done = prog?.done ?? 0;
                  const total = prog?.total ?? 0;
                  const percent = prog?.percent ?? 0;

                  const barClass = barClassFor(percent);
                  const label = total > 0 ? `${done}/${total}` : `${done}/—`;

                  return (
                    <div key={a.id} className="card mb-3 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="fw-semibold mb-1">{a.aspirant_name}</h6>
                            <div className="mb-2">
                              <small className="text-muted d-block">Matricule:</small>
                              {a.matricule ? (
                                <span className="badge bg-success text-white">{a.matricule}</span>
                              ) : (
                                <span className="badge bg-warning text-dark">En attente</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="badge bg-info text-dark">{formatParcours(a.parcours)}</span>
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">Âge:</small>
                          <span>{calcAge(a.birth_date)}</span>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Progression:</small>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="text-muted">{label} ({percent}%)</small>
                          </div>
                          <div className="progress" style={{ height: 18 }}>
                            <div
                              className={`progress-bar ${barClass}`}
                              role="progressbar"
                              style={{ width: `${percent}%` }}
                              aria-valuenow={percent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setSelectedEglise(null);
                  setAspirants([]);
                  setProgressCGByAspirant({});
                  setProgressSYLByAspirant({});
                }}
              >
                ← Retour aux Églises
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
